import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Mail, Lock, ArrowRight, Sparkles, Activity, Calendar, MessageSquare, ShieldCheck } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function HomePage() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const u = await login(email.trim(), password);
      nav(u.role === "patient" ? "/m" : "/dashboard");
    } catch (e2) {
      const msg = e2.response?.data?.detail
        || (e2.response ? `HTTP ${e2.response.status}` : e2.message)
        || "Login failed";
      // eslint-disable-next-line no-console
      console.error("[login] failed:", { msg, status: e2.response?.status, data: e2.response?.data });
      setErr(msg);
    } finally { setBusy(false); }
  };

  const fillDemo = (em, pw) => { setEmail(em); setPassword(pw); setErr(""); };
  return (
    <div className="relative">
      <section className="bg-grid">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 pt-20 pb-24 grid lg:grid-cols-[1.15fr_1fr] gap-16 items-center">
          {/* LEFT — Hero copy + 3D ring */}
          <div className="relative">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-6">
              <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-xs font-semibold tracking-wider text-emerald-300 uppercase">Chemotherapy Treatment Companion</span>
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.05] mb-6 fade-up">
              The bidirectional cadence
              <br />
              of <span className="text-emerald-400">cancer care.</span>
            </h1>
            <p className="text-lg text-slate-400 max-w-xl leading-relaxed mb-8 fade-up" style={{ animationDelay: "0.15s" }}>
              OncoCadence unites oncology providers and their patients on a single, real-time platform. Build chemotherapy regimens once, generate accurate per-patient calendars in seconds, and stay in sync from first cycle to last dose.
            </p>
            <div className="flex flex-wrap items-center gap-4 mb-12 fade-up" style={{ animationDelay: "0.3s" }}>
              <Link to="/demo" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-[#0B1220] font-bold transition-colors shadow-xl shadow-emerald-500/30" data-testid="hero-cta-demo">
                Request a demo <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/how-it-works" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-white/10 text-slate-200 hover:border-emerald-500/40 hover:text-white transition-colors">
                See how it works
              </Link>
            </div>
            <div className="grid grid-cols-3 gap-6 max-w-md">
              <Stat n="187" label="Oncology drugs" />
              <Stat n="40+" label="Standard regimens" />
              <Stat n="24/7" label="Patient access" />
            </div>
            {/* Decorative ring behind hero (3D-feeling, CSS only) */}
            <div className="absolute -top-12 -left-24 -z-10 w-[480px] opacity-50 hidden lg:block">
              <div className="marketing-3d-ring">
                <div className="ring r1" /><div className="ring r2" /><div className="ring r3" /><div className="ring r4" /><div className="core" />
              </div>
            </div>
          </div>

          {/* RIGHT — Embedded sign-in card (mirrors /login exactly) */}
          <div className="glass-card p-7 md:p-9 max-w-md w-full justify-self-end fade-up" style={{ animationDelay: "0.45s" }} data-testid="home-login-card">
            <h2 className="text-2xl font-bold mb-1">Welcome back</h2>
            <p className="text-[11px] uppercase tracking-[0.18em] text-emerald-400 font-bold mb-6">Chemotherapy Treatment Companion</p>
            <form onSubmit={submit} className="space-y-4" autoComplete="on">
              <Field icon={Mail} type="email" placeholder="you@chemo.app" value={email} onChange={setEmail} testid="home-login-email" autoComplete="username" />
              <Field icon={Lock} type="password" placeholder="••••••••••" value={password} onChange={setPassword} testid="home-login-password" autoComplete="current-password" />
              {err && <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="home-login-error">{err}</div>}
              <button type="submit" disabled={busy} className="w-full py-3 rounded-lg bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-[#0B1220] font-bold transition-colors" data-testid="home-login-submit">
                {busy ? "Signing in…" : "Sign in"}
              </button>
            </form>
            <div className="flex justify-between text-xs text-slate-400 mt-4">
              <Link to="/register" className="hover:text-emerald-400">Create account</Link>
              <Link to="/reset-password" className="hover:text-emerald-400">Forgot password?</Link>
            </div>
            <div className="mt-6 pt-6 border-t border-white/[0.06]">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold mb-3">Demo accounts</div>
              <div className="grid grid-cols-2 gap-2">
                <DemoChip role="Doctor"  email="doctor@chemo.app"  pw="Doctor@123"  onPick={fillDemo} />
                <DemoChip role="Admin"   email="admin@chemo.app"   pw="Admin@123"   onPick={fillDemo} />
                <DemoChip role="Nurse"   email="nurse@chemo.app"   pw="Nurse@123"   onPick={fillDemo} />
                <DemoChip role="Patient" email="patient@chemo.app" pw="Patient@123" onPick={fillDemo} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick-feature triplet */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-20">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-500 font-bold mb-3">One platform, two perspectives</div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Built bidirectional from day one.</h2>
          <p className="text-slate-400 mt-3">Every change a provider makes is reflected on the patient&apos;s mobile app in real time, and every patient request flows straight back into the provider&apos;s approvals queue.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          <HomeTile icon={Calendar} title="Treatment calendars in seconds" body="Pick a regimen, set a start date, OncoCadence schedules every drug administration, lab draw, and clinic visit with cycle-accurate dependencies." />
          <HomeTile icon={MessageSquare} title="Two-way patient messaging" body="In-app secure messaging plus optional SMS notifications via Twilio. Providers see unread counts; patients tap and reply from their phone." />
          <HomeTile icon={ShieldCheck} title="HIPAA-ready architecture" body="JWT sessions, bcrypt password hashing, role-based access control, and a clean audit trail across every patient request." />
        </div>
      </section>

      {/* Closing CTA */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 pb-20">
        <div className="glass-card p-10 md:p-14 text-center">
          <Activity className="w-10 h-10 text-emerald-400 mx-auto mb-4" />
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Ready to bring your chemo schedule into rhythm?</h2>
          <p className="text-slate-400 mt-3 max-w-xl mx-auto">A 30-minute demo walks you through Admin, Doctor, Nurse, and Patient flows — all four user roles, all four real demo accounts.</p>
          <Link to="/demo" className="inline-flex items-center gap-2 mt-7 px-6 py-3 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-[#0B1220] font-bold transition-colors">
            Book your demo <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}

function Stat({ n, label }) {
  return (
    <div>
      <div className="text-3xl font-bold text-emerald-400 tabular-nums">{n}</div>
      <div className="text-xs text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}

function Field({ icon: Icon, type, placeholder, value, onChange, testid, autoComplete }) {
  return (
    <div className="relative">
      <Icon className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
      <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        data-testid={testid}
        className="w-full pl-10 pr-3 py-3 rounded-lg bg-white/[0.04] border border-white/10 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 text-slate-100 placeholder:text-slate-500 transition-colors"
      />
    </div>
  );
}

function DemoChip({ role, email, pw, onPick }) {
  return (
    <button type="button" onClick={() => onPick(email, pw)} className="text-left px-3 py-2 rounded-lg border border-white/[0.06] hover:border-emerald-500/30 hover:bg-emerald-500/[0.04] transition-colors" data-testid={`demo-${role.toLowerCase()}`}>
      <div className="text-[11px] font-bold text-emerald-400 mb-0.5">{role}</div>
      <div className="text-[10px] text-slate-500 truncate">{email}</div>
    </button>
  );
}

function HomeTile({ icon: Icon, title, body }) {
  return (
    <div className="glass-card glass-card-hover p-7">
      <div className="w-11 h-11 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-4">
        <Icon className="w-5 h-5 text-emerald-400" />
      </div>
      <h3 className="font-bold text-lg mb-2">{title}</h3>
      <p className="text-sm text-slate-400 leading-relaxed">{body}</p>
    </div>
  );
}
